/**
 * Разбор одной работы — стеклянной панелью поверх экрана.
 *
 * Владелец: «нужно раскрывать что там было — в модалке подробный отчёт».
 * Повод конкретный и дорогой. Повторный разворот показал в списке «2 556
 * файлов · 22 ГБ», и по этой строке нельзя было понять ни почему двадцать два
 * гигабайта вместо шести, ни почему двадцать восемь минут вместо десяти, ни —
 * главное — что базу данных никто не восстанавливал.
 *
 * Отсюда три раздела, и порядок их не случаен:
 *
 *   1. **чем кончилось** — то, за чем открыли;
 *   2. **чего НЕ делали** — потому что умолчание читается как «сделано», а
 *      «дамп разложен файлом» и «база поднята» — это разные вещи, и путать их
 *      в день переключения нельзя;
 *   3. **как шло** — лента по главам, из которой и видно, куда ушло время.
 */
import { useEffect, useState } from 'react';
import { request } from '../transport/socket.ts';
import { bytes, count } from '../lib/format.ts';
import { forHuman } from '../lib/rate.ts';

interface Chapter {
  kind: 'prep' | 'write' | 'check';
  from: string; to: string; seconds: number;
  wrote: number; net: number; note: string | null; files: number;
}

/**
 * Портрет копии: что она отдаёт, если её спросить как сайт.
 *
 * Всё остальное в этом отчёте — про НАШУ работу: файлы разложены, дамп залит,
 * контейнеры подняты. Ни одно из этих утверждений не про магазин, а DNS
 * переключают именно на магазин, и магазин с чужой конфигурацией отвечает
 * двумястами так же бодро, как исправный.
 *
 * Приёмник спрашивает страницы прямо у контейнера, минуя сертификат, и
 * сравнивает с живым сайтом (их 276). Сравнение и есть ответ: не «двести», а
 * «отдаёт то же, что оригинал».
 */
interface SiteCheck {
  ok?: boolean;
  address?: string;
  container?: string;
  host?: string;
  error?: string;
  urls?: {
    path: string; ok: boolean; status: number; bytes: number; ms: number;
    title?: string; why?: string[];
    counts?: { products: number; prices: number; links: number; forms: number };
  }[];
  compare?: {
    url: string; status: number; bytes: number; title?: string;
    sameTitle?: boolean; sizeRatio?: number; note: string;
  };
  /** Первые килобайты разметки главной. Только текстом — см. довод ниже. */
  excerpt?: string;
}

interface Report {
  restore: {
    restoreId: string; snapshotId: string; siteId: string; into: string;
    state: string; mode: string; sources: string[];
    requestedAt: string; startedAt: string | null; finishedAt: string | null;
    progress: {
      filesDone: number; filesTotal?: number | null;
      bytesWritten?: number | null; bytesNet?: number | null;
      bytesUploaded: number; bytesRead: number;
      packsDone?: number | null; packsRefetched?: number | null;
    };
    error: { code: string; msg: string } | null;
  };
  targetLabel: string;
  plan: { files: number; dirs: number; bytes: number; chunks: number } | null;
  expectBytes: number | null;
  freeBytes: number | null;
  keep: string[];
  prune: boolean;
  restart: boolean;
  importDumps: boolean;
  mismatches: string[];
  /** Слово приёмника о том, что он сделал и что потерял. Форму не знаем. */
  outcome: Record<string, unknown> | null;
  chapters: Chapter[];
  /** Всё, что записано про эту работу: наши события и строки приёмника. */
  log: { at: string; level: string; type: string; msg: string }[];
  /** Записано файлов — по кадру записи, а не по последнему пришедшему. */
  filesWritten: number | null;
  checkingSeconds: number;
  rewrittenBytes: number;
}

/** Слова приёмника о сделанном — по-человечески, но без выдумок. */
const APPLIED_WORD: Record<string, string> = {
  siteCheck: 'проверка сайта',
  dumpImport: 'дамп загружен в базу',
  restarted: 'контейнеров перезапущено',
  services: 'службы',
  prepare: 'подготовка до дампа',
  prepareAfter: 'подготовка после дампа',
};

/** Значение неизвестной формы — одной строкой, без раскрытия дерева. */
function shortly(v: unknown): string {
  if (v == null) return '—';
  if (typeof v === 'object') {
    const s = JSON.stringify(v);
    return s.length > 200 ? `${s.slice(0, 200)}…` : s;
  }
  return String(v);
}

const KIND_WORD: Record<Chapter['kind'], string> = {
  prep: 'подготовка',
  write: 'запись',
  check: 'проверка',
};

/**
 * Заголовок страницы — человеку, а не браузеру.
 *
 * Приёмник берёт его из разметки как есть, и апостроф приезжает как `&#039;`:
 * «Search results for: &#039;shoe&#039;». Владельцу показывают заголовок его
 * магазина, а не то, как он закодирован, — и в строке, по которой он решает,
 * тот ли это сайт, лишние закорючки читаются как поломка.
 *
 * Разворачиваем только именованные сущности и числовые коды, ничего не
 * исполняя: это текст, и текстом он и останется.
 */
const ENTITIES: Record<string, string> = {
  amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ', laquo: '«', raquo: '»',
  mdash: '—', ndash: '–', hellip: '…',
};

function plain(text: string): string {
  return text.replace(/&(#x?[0-9a-f]+|[a-z]+);/gi, (whole, body: string) => {
    if (body.startsWith('#')) {
      const code = body[1]?.toLowerCase() === 'x'
        ? Number.parseInt(body.slice(2), 16)
        : Number.parseInt(body.slice(1), 10);
      return Number.isFinite(code) && code > 0 && code < 0x110000
        ? String.fromCodePoint(code) : whole;
    }
    return ENTITIES[body.toLowerCase()] ?? whole;
  });
}

function clock(iso: string): string {
  return new Date(iso).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

/**
 * Этапы работы по порядку — чтобы видеть не только пройденное, но и предстоящее.
 *
 * Владелец: «нужно показывать работу на ВСЕХ этапах, никакого простоя». Половина
 * простоя — это незнание: экран молчит не потому, что нечего сказать, а потому
 * что не знает, что сейчас должно происходить. Список этапов знаем мы — он
 * следует из тикета, — и показать его можно с первой секунды.
 *
 * Отмечается пройденное тем, что о нём есть след: фаза в ленте или строка
 * журнала. Чего нет — то впереди, и так и написано.
 */
const STAGES: { key: string; word: string; hint: string; phases: string[] }[] = [
  {
    key: 'survey', word: 'Перечень и опись',
    hint: 'читает перечень снимка и опись приёмника одновременно',
    phases: ['survey', 'manifest'],
  },
  {
    key: 'compare', word: 'Сверка',
    hint: 'сравнивает снимок с тем, что уже на диске; сеть при этом не трогает',
    phases: ['compare'],
  },
  {
    key: 'restore', word: 'Запись файлов',
    hint: 'качает недостающее и раскладывает по местам',
    phases: ['restore', 'chunks'],
  },
  {
    key: 'verify', word: 'Проверочные круги',
    hint: 'снимает опись заново и дописывает недостающее — до четырёх кругов',
    phases: ['verify'],
  },
  {
    key: 'prepare', word: 'Подготовка конфигурации',
    hint: 'переписывает адреса базы, сессий и поиска на здешние — копией поверх',
    phases: ['prepare'],
  },
  {
    key: 'backup', word: 'Запасной дамп',
    hint: 'снимает текущее состояние базы, чтобы было чем отменить',
    phases: ['backup'],
  },
  {
    key: 'import', word: 'Заливка базы',
    hint: 'льёт дамп из снимка; самый долгий шаг, около полутора часов',
    phases: ['import', 'dump'],
  },
  {
    key: 'finalize', word: 'Шаги после дампа',
    hint: 'движок поиска, индексы, сброс кеша',
    phases: ['finalize'],
  },
  {
    key: 'restart', word: 'Пересоздание контейнеров',
    hint: 'поднимает проект заново — иначе он держится за прежнюю конфигурацию',
    phases: ['restart'],
  },
  {
    key: 'check', word: 'Проверка сайта',
    hint: 'запрашивает страницы прямо у контейнера и сверяет их с живым сайтом',
    phases: ['check'],
  },
];

export function RestoreReport({ restoreId, onClose }: {
  restoreId: string;
  onClose: () => void;
}): React.JSX.Element {
  const [data, setData] = useState<Report | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const r = await request<Report>('restore.report', { restoreId });
        if (alive) setData(r);
      } catch (err) {
        if (alive) setFailure((err as { msg?: string }).msg ?? 'не получилось');
      }
    })();
    return () => { alive = false; };
  }, [restoreId]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const applied = data?.outcome?.['applied'] as Record<string, unknown> | undefined;
  const site = applied?.['siteCheck'] as SiteCheck | undefined;
  const lost = data?.outcome ?? null;
  const p = data?.restore.progress;
  const written = p?.bytesWritten ?? p?.bytesUploaded ?? 0;
  const net = p?.bytesNet ?? null;
  const took = data?.restore.startedAt && data.restore.finishedAt
    ? (new Date(data.restore.finishedAt).getTime()
      - new Date(data.restore.startedAt).getTime()) / 1000
    : null;

  return (
    <div className="modal" role="presentation" onClick={onClose}>
      <div className="modal__box report" role="dialog" aria-modal="true"
           aria-labelledby="report-title" onClick={(e) => e.stopPropagation()}>
        <h3 className="modal__title" id="report-title">Как прошла работа</h3>

        {failure && <p className="alert alert--error">{failure}</p>}
        {!data && !failure && <p className="muted">читаю…</p>}

        {data && (
          <div className="modal__body report__body">
            <p className="muted report__lead">
              {data.restore.siteId} → {data.targetLabel}
              {', '}<code className="mono">{data.restore.into}</code>
              {', снимок '}<span className="mono">{data.restore.snapshotId.slice(5, 24)}</span>
            </p>

            <dl className="report__facts">
              <div>
                <dt>заняла</dt>
                <dd>
                  {took != null
                    ? forHuman(took)
                    : data.restore.startedAt
                      ? <>идёт {forHuman(
                        (Date.now() - new Date(data.restore.startedAt).getTime()) / 1000)}</>
                      : '—'}
                </dd>
              </div>
              <div>
                <dt>записано на диск</dt>
                <dd>{bytes(written)}</dd>
              </div>
              <div>
                <dt>пришло из сети</dt>
                <dd>
                  {net != null ? bytes(net) : '—'}
                  {/*
                    Отношение показываем, только когда оно что-то значит.
                    «×0.0» под двумя мегабайтами, приехавшими одним паком на
                    135 МБ, — это не выигрыш дедупликации и не перекос, а
                    арифметика на слишком малых числах.
                  */}
                  {net != null && net > 0 && written > 2e9 && (
                    <span className="muted"> · ×{(written / net).toFixed(1)}</span>
                  )}
                </dd>
              </div>
              <div>
                <dt>файлов записано</dt>
                {/*
                  Из кадра ЗАПИСИ, а не из последнего пришедшего.

                  Работа, оборвавшаяся на сверке, оставляет в документе
                  миллионы ОСМОТРЕННЫХ записей. Разбор писал «файлов записано
                  4 194 304» при одном записанном файле — и это не описка, а
                  прямая неправда о том, что случилось с чужой машиной.
                */}
                <dd>{count(data.filesWritten ?? p?.filesDone ?? 0)}</dd>
              </div>
            </dl>

            {/*
              Переписанное — главное число разбора, если оно есть.
              Двадцать два гигабайта при работе на шесть объясняются только
              здесь, и назвать это надо прямо, а не оставить читателю
              вычитание.
            */}
            {data.rewrittenBytes > 0 && (
              <p className="report__note">
                Сверх первого прохода переписано <b>{bytes(data.rewrittenBytes)}</b>
                {data.checkingSeconds > 0 && <> · на проверочные круги ушло{' '}
                  <b>{forHuman(data.checkingSeconds)}</b></>}. Приёмник после записи
                снимает опись заново и дописывает то, что разошлось; каждый такой
                круг обходит всё дерево целиком.
              </p>
            )}

            {/*
              Слово приёмника — раньше наших выводов.

              Он единственный, кто видел диск. Если он говорит «дамп загружен»
              или «подготовка прошла», это факт, а наши строки ниже — вывод из
              тикета. Ставить вывод впереди факта значит однажды сказать
              «база не восстанавливалась» о работе, в которой она была
              восстановлена.
            */}
            {applied && (
              <>
                <h4 className="report__head">Что приёмник сделал сверх файлов</h4>
                <ul className="report__not">
                  {Object.entries(applied).map(([k, v]) => (
                    <li key={k}>
                      <b>{APPLIED_WORD[k] ?? k}</b>
                      {': '}
                      <span className="mono">{shortly(v)}</span>
                    </li>
                  ))}
                </ul>
              </>
            )}

            {/*
              Три списка, а не один, и заголовок у каждого свой.
              
              Было хуже некуда: раздел назывался «чего эта работа НЕ делала», а
              под ним стояло «дамп загружен в базу — данные заменены копией из
              снимка». Владелец справедливо сказал «что-то не очень логично».
              
              И это была не только путаница в заголовке. Строка утверждала
              СДЕЛАННОЕ по тому, что было ЗАКАЗАНО: `importDumps` в тикете
              означает «просили залить», а не «залили». Работа в тот момент ещё
              шла — база заливалась прямо в эти минуты, — а карточка уже
              сообщала, что данные заменены. О самой разрушительной операции в
              системе так писать нельзя.
              
              Теперь источник правды — слово приёмника: `applied` говорит, что
              он сделал. Заказанное, но не подтверждённое, стоит отдельно и
              честно: «просили — ещё не сделано» у идущей работы, «просили — не
              сделал» у законченной.
            */}
            {(() => {
              const done: string[] = [];
              const planned: string[] = [];
              const skipped: string[] = [];
              const running = data.restore.state === 'running'
                || data.restore.state === 'granted';
              const pending = (what: string): string => running
                ? `${what} — просили, идёт или ещё впереди.`
                : `${what} — просили, но приёмник об этом не отчитался.`;

              if (applied?.dumpImport != null) {
                /*
                 * Что НЕ залито — рядом с тем, что залито, и всегда.
                 *
                 * Их довод из 278: «не залито 3,1 ГБ из 3,8» должно быть
                 * видно, иначе через месяц никто не вспомнит, почему в журнале
                 * поиска пусто, и будут искать поломку.
                 */
                const imp = applied.dumpImport as { skipped?: { bytes?: number;
                  tables?: number } } | null;
                const cut = imp?.skipped;
                done.push('Дамп загружен в базу — данные заменены копией из снимка.'
                  + (cut?.bytes
                    ? ` Не залито ${(cut.bytes / 1e9).toFixed(1)} ГБ`
                      + `${cut.tables ? ` в ${cut.tables} таблицах` : ''} — по вашему выбору: `
                      + 'таблицы созданы пустыми.'
                    : ''));
              } else if (data.importDumps) {
                planned.push(pending('Загрузка дампа в базу'));
              } else {
                skipped.push('База данных НЕ восстанавливалась. Дамп разложен файлом '
                  + 'в .dumps/ внутри каталога; чтобы он стал базой, его надо загрузить.');
              }

              if (applied?.restarted != null) {
                done.push(`Контейнеры проекта подняты заново: ${String(applied['restarted'])}.`);
              } else if (data.restart) {
                planned.push(pending('Пересоздание контейнеров'));
              } else {
                skipped.push('Контейнеры не трогали: ни останова, ни запуска. Что было '
                  + 'поднято на приёмнике, то и осталось.');
              }

              if (applied?.prepare != null || applied?.prepareAfter != null) {
                done.push('Подготовка конфигурации выполнена: адреса базы, сессий и '
                  + 'поиска переписаны на здешние.');
              }

              if (data.prune) {
                (data.restore.state === 'ok' ? done : planned).push(
                  'Лишнее в каталоге удаляется: он приводится к снимку.');
              } else {
                skipped.push('Чужие файлы в каталоге не удалялись — то, чего нет в '
                  + 'снимке, осталось на месте.');
              }

              return (
                <>
                  {done.length > 0 && (
                    <>
                      <h4 className="report__head">Что сделано</h4>
                      <ul className="report__not">
                        {done.map((x) => <li key={x}>{x}</li>)}
                      </ul>
                    </>
                  )}
                  {planned.length > 0 && (
                    <>
                      <h4 className="report__head">
                        {running ? 'Ещё впереди' : 'Просили, но не подтверждено'}
                      </h4>
                      <ul className="report__not">
                        {planned.map((x) => <li key={x}>{x}</li>)}
                      </ul>
                    </>
                  )}
                  <h4 className="report__head">Чего эта работа не делала</h4>
                  <ul className="report__not">
                    {skipped.map((x) => <li key={x}>{x}</li>)}
                    {data.keep.length > 0 && (
                      <li>
                        Не трогали по вашему указанию:{' '}
                        {data.keep.map((k) => <code key={k} className="mono">{k}</code>)
                          .reduce<React.ReactNode[]>((a, x) => a.length ? [...a, ', ', x] : [x], [])}
                      </li>
                    )}
                  </ul>
                </>
              );
            })()}

            {data.mismatches.length > 0 && (
              <p className="alert alert--error">
                Не сошлись суммы у {count(data.mismatches.length)} файлов:{' '}
                <span className="mono">{data.mismatches.slice(0, 5).join(', ')}</span>
                {data.mismatches.length > 5 && ' …'}
              </p>
            )}

            {/*
              Потери — рядом с успехом, а не вместо него.

              Восстановление, закончившееся словом «готово», но потерявшее
              списки доступа или снёсшее симлинки, — это успех по форме. Молчать
              о таком нельзя: молчаливая потеря хуже громкого отказа, потому
              что отказ виден.
            */}
            {lost != null && (lost['xattrSkipped'] != null || lost['replacedLinks'] != null
              || lost['ownerUnknown'] != null || lost['webRootWarning'] != null) && (
              <>
                <h4 className="report__head">Что восстановилось не один в один</h4>
                <ul className="report__not">
                  {lost['xattrSkipped'] != null && (
                    <li>
                      Не выставлено расширенных атрибутов: {String(lost['xattrSkipped'])}.
                      Среди них бывают списки доступа — права по chmod при этом
                      сходятся, а доступы теряются.
                    </li>
                  )}
                  {lost['replacedLinks'] != null && (
                    <li>
                      Снесено символических ссылок ради каталогов на их местах:{' '}
                      {String(lost['replacedLinks'])}. По такой ссылке могли уезжать
                      данные в сторону — место потратится заново, а внешне всё хорошо.
                    </li>
                  )}
                  {Array.isArray(lost['ownerUnknown']) && lost['ownerUnknown'].length > 0 && (
                    <li>
                      Не нашлось имён владельцев: {lost['ownerUnknown'].length}.
                      Номера переложить было не по чему.
                    </li>
                  )}
                  {lost['webRootWarning'] != null && (
                    <li className="wizard__bad">{String(lost['webRootWarning'])}</li>
                  )}
                </ul>
              </>
            )}

            {site && (
              <>
                <h4 className="report__head">
                  Проверка сайта{site.ok === false ? ' — НЕ ПРОЙДЕНА' : ''}
                </h4>
                {site.error && <p className="alert alert--error">{site.error}</p>}
                {site.address && (
                  <p className="muted report__lead">
                    Спрашивали прямо у контейнера{' '}
                    <span className="mono">{site.container ?? ''}</span> по адресу{' '}
                    <span className="mono">{site.address}</span>, подставив имя{' '}
                    <span className="mono">{site.host ?? ''}</span> — сертификат для этого
                    не нужен, стучимся до него.
                  </p>
                )}
                <ul className="report__pages">
                  {(site.urls ?? []).map((u) => (
                    <li key={u.path} className={u.ok ? '' : 'wizard__bad'}>
                      <span className="mono">{u.path}</span>
                      {u.title && <> — «{plain(u.title)}»</>}
                      <span className="muted">
                        {' '}· {u.status} · {Math.round(u.bytes / 1024)} КБ · {u.ms} мс
                        {u.counts && u.counts.products > 0 && `, товаров ${u.counts.products}`}
                        {u.counts && u.counts.prices > 0 && `, цен ${u.counts.prices}`}
                        {u.counts && u.counts.links > 0 && `, ссылок ${u.counts.links}`}
                        {u.counts && u.counts.forms > 0 && `, форм ${u.counts.forms}`}
                      </span>
                      {u.why?.length ? <span className="wizard__bad"> — {u.why.join('; ')}</span> : null}
                    </li>
                  ))}
                </ul>
                {/*
                  Сравнение с живым — единственная строка здесь, которая
                  говорит о магазине, а не о нашей работе.
                */}
                {site.compare && (
                  <p className="report__note">
                    Сравнение с <span className="mono">{site.compare.url}</span>:{' '}
                    {site.compare.sameTitle
                      ? `заголовок совпал${site.compare.title
                        ? ` — «${plain(site.compare.title)}»` : ''}`
                      : 'ЗАГОЛОВОК НЕ СОВПАЛ'}
                    {site.compare.sizeRatio != null
                      && `, размер ${site.compare.sizeRatio.toFixed(2)} от оригинала`}
                    {`, оригинал ответил ${site.compare.status}`}.
                    {site.compare.note ? ` ${site.compare.note}` : ''}
                  </p>
                )}
                {site.excerpt && (
                  <details className="report__excerpt">
                    <summary>Разметка главной, первые килобайты</summary>
                    {/*
                      Текстом, и только текстом.

                      Их предупреждение из 276, и оно верное: в живом кадре
                      браузер владельца подтянет картинки и стили по БОЕВЫМ
                      адресам, и страница получится красивая и лживая — она
                      докажет, что работает старая машина, а не копия.
                    */}
                    <pre className="mono report__raw">{site.excerpt}</pre>
                  </details>
                )}
              </>
            )}

            <h4 className="report__head">Этапы</h4>
            {/*
              Пройденное и предстоящее в одном списке.

              «Никакого простоя» начинается с того, чтобы владелец видел, где
              работа находится, — а не только то, что она молчит.
            */}
            <ol className="report__stages">
              {STAGES.map((st) => {
                const seen = data.chapters.some((c) => c.note && st.phases.some(
                  (ph) => c.note!.includes(ph),
                )) || data.log.some((l) => st.phases.some((ph) => l.msg.includes(ph)));
                const deployStage = ['prepare', 'backup', 'import', 'finalize', 'restart']
                  .includes(st.key);
                const asked = !deployStage || data.importDumps || data.restart;
                if (!asked) return null;
                return (
                  <li key={st.key} className={`report__stage${seen ? ' report__stage--done' : ''}`}>
                    <b>{st.word}</b>
                    <span className="muted"> — {st.hint}</span>
                  </li>
                );
              })}
            </ol>

            <h4 className="report__head">Как шло</h4>
            {data.chapters.length === 0 ? (
              <p className="muted">
                Лента этой работы не записывалась — она шла до того, как консоль
                научилась её вести. Остались итоги выше.
              </p>
            ) : (
              <ol className="report__story">
                {data.chapters.map((c) => (
                  <li key={c.from} className={`report__chapter report__chapter--${c.kind}`}>
                    <span className="report__when mono">{clock(c.from)}</span>
                    <span className="report__what">
                      <b>{KIND_WORD[c.kind]}</b> · {forHuman(c.seconds)}
                      {c.wrote > 0 && <> · записано {bytes(c.wrote)}</>}
                      {c.kind === 'check' && <> · записанное не менялось</>}
                      {c.note && <span className="muted report__said"> {c.note}</span>}
                    </span>
                  </li>
                ))}
              </ol>
            )}

            {/*
              Полная лента — словами приёмника, а не нашим пересказом.

              Всё, что он говорит о себе, приходит `agent.log` и лежало у нас с
              самого начала: просто номер работы он пишет в тексте фразы, а не
              в поле, и лента его строк не находила. Теперь находит — и это
              самое подробное, что вообще есть о работе.
            */}
            <h4 className="report__head">
              Полный лог{data.log.length > 0 ? ` · ${data.log.length} строк` : ''}
            </h4>
            {data.log.length === 0 ? (
              <p className="muted">
                Приёмник об этой работе ничего не рассказывал. Это не значит, что
                ничего не было: строки журнала он шлёт по своему усмотрению, и
                самый долгий этап — развёртывание — пока идёт молча.
              </p>
            ) : (
              <ol className="report__log">
                {data.log.map((l, i) => (
                  <li key={`${l.at}-${i}`} className={`report__line report__line--${l.level}`}>
                    <span className="report__when mono">{clock(l.at)}</span>
                    <span className="report__msg">{l.msg}</span>
                  </li>
                ))}
              </ol>
            )}

            {data.plan && (
              <p className="muted report__tail">
                Приёмник брался за {count(data.plan.files)} файлов и{' '}
                {bytes(data.plan.bytes)}; мы ожидали не больше{' '}
                {data.expectBytes != null ? bytes(data.expectBytes) : '—'}, свободно
                у него было {data.freeBytes != null ? bytes(data.freeBytes) : '—'}.
              </p>
            )}
          </div>
        )}

        <div className="modal__actions">
          <button type="button" className="button" onClick={onClose}>Закрыть</button>
        </div>
      </div>
    </div>
  );
}
